import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Bot, 
  Settings, 
  Play, 
  Square, 
  TrendingUp, 
  Target,
  ChevronDown,
  ChevronUp,
  Activity,
  AlertCircle,
  Zap,
  BarChart3,
  Save
} from 'lucide-react';

const TradingBot = ({ selectedGroupId, groupName }) => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [botStatus, setBotStatus] = useState('inactive');
  const [activeBotId, setActiveBotId] = useState(null); // Track actual bot instance ID
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  
  // Token analysis state
  const [tokenAnalysis, setTokenAnalysis] = useState(null);
  const [analyzingToken, setAnalyzingToken] = useState(false);
  const [lastAnalyzedToken, setLastAnalyzedToken] = useState('');
  
  // Refs for debouncing and race condition handling
  const debounceTimeoutRef = useRef(null);
  const currentAnalysisRef = useRef(null);
  const [stopCooldown, setStopCooldown] = useState(false); // Prevent status checks after stop
  const [parameters, setParameters] = useState({
    whale_trade_percent_min: 0.02,
    whale_trade_percent_max: 0.05,
    retail_trade_percent_min: 0.10,
    retail_trade_percent_max: 0.20,
    whale_buy_probability: 0.70,
    retail_buy_probability: 0.60,
    whale_interval_min: 600,
    whale_interval_max: 86400,
    retail_interval_min: 600,
    retail_interval_max: 86400,
    whale_threshold: 5.0,
    whale_threshold_usdc: 100.0,
    price_range_min: 0,
    price_range_max: 100,
    mint_address: '',
    status: 'inactive',
    // Advanced parameters
    hour_threshold: 0.01,
    day_threshold: 0.05,
    volatility_multiplier: 5,
    aggressiveness: 0.5,
    use_advanced_settings: false,
    use_usdc_trading: false
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      console.log(`TradingBot: Switching to group ${selectedGroupId}`);
      
      // Reset state when switching groups
      setBotStatus('inactive');
      setActiveBotId(null);
      setError('');
      setSaveStatus('');
      
      // Fetch data sequentially to avoid race conditions
      const loadGroupData = async () => {
        try {
          // Fetch parameters first
          await fetchParameters();
          // Then fetch bot status
          await fetchBotStatus();
          console.log(`TradingBot: Data loaded for group ${selectedGroupId}`);
        } catch (error) {
          console.error(`TradingBot: Error loading data for group ${selectedGroupId}:`, error);
        }
      };
      
      loadGroupData();
      
      // Set up periodic refresh for bot status
      const interval = setInterval(() => {
        if (!stopCooldown) {
          fetchBotStatus();
        }
      }, 10000); // Refresh every 10 seconds (reduced frequency)
      
      return () => {
        clearInterval(interval); // Clean up on unmount or group change
        console.log(`TradingBot: Cleaned up interval for group ${selectedGroupId}`);
      };
    }
  }, [selectedGroupId]);

  const fetchParameters = async () => {
    try {
      console.log(`TradingBot: Fetching parameters for group ${selectedGroupId}`);
      const response = await api.get('/trading-bot/trading-parameters', {
        params: { groupId: selectedGroupId }
      });
      console.log(`TradingBot: Got parameters for group ${selectedGroupId}:`, response.data);
      setParameters(response.data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
    }
  };

  const fetchBotStatus = async () => {
    try {
      // Skip status check if in stop cooldown
      if (stopCooldown) {
        console.log(`TradingBot: Skipping status check due to stop cooldown`);
        return;
      }
      
      console.log(`TradingBot: Fetching bot status for group ${selectedGroupId}`);
      const response = await api.get('/trading-bot/bot/status', {
        params: { groupId: selectedGroupId }
      });
      const activeBots = response.data.bots;
      console.log(`TradingBot: Found ${activeBots.length} active bots for group ${selectedGroupId}:`, activeBots);
      
      // Find bot for current group - use groupId directly instead of parameters.id
      // This avoids race conditions when switching groups
      const currentBot = activeBots.find(bot => 
        bot.wallet_group_id === selectedGroupId
      );
      
      if (currentBot) {
        console.log(`TradingBot: Found matching bot for group ${selectedGroupId}:`, currentBot);
        // Only update status if it's different to prevent unnecessary re-renders
        if (botStatus !== currentBot.status) {
          setBotStatus(currentBot.status);
        }
        if (activeBotId !== currentBot.id) {
          setActiveBotId(currentBot.id); // Store the actual bot instance ID
        }
      } else {
        console.log(`TradingBot: No matching bot found for group ${selectedGroupId}`);
        // Only update if currently showing as active
        if (botStatus !== 'inactive') {
          setBotStatus('inactive');
        }
        if (activeBotId !== null) {
          setActiveBotId(null);
        }
      }
    } catch (error) {
      console.error('Error fetching bot status:', error);
      // Only update on error if not already inactive
      if (botStatus !== 'inactive') {
        setBotStatus('inactive');
      }
      if (activeBotId !== null) {
        setActiveBotId(null);
      }
    }
  };

  const handleParameterChange = (e) => {
    const { name, value } = e.target;
    
    // Allow any value during typing (including empty strings)
    setParameters(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    
    // Trigger debounced token analysis when mint_address changes
    if (name === 'mint_address') {
      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      if (value && value.length > 40) { // Basic validation for Solana addresses
        // Debounce the analysis by 300ms to avoid too many API calls
        debounceTimeoutRef.current = setTimeout(() => {
          analyzeTokenDebounced(value);
        }, 300);
      } else {
        // Clear analysis immediately if invalid token address
        setTokenAnalysis(null);
        setLastAnalyzedToken('');
        setAnalyzingToken(false);
      }
    }
  };

  // Debounced token analysis function with race condition handling
  const analyzeTokenDebounced = async (mintAddress) => {
    if (!mintAddress || mintAddress.length <= 40) {
      return;
    }

    // Always analyze - don't skip if it's the same token (user might be re-checking)
    // if (mintAddress === lastAnalyzedToken && tokenAnalysis && tokenAnalysis.status !== 'error') {
    //   console.log('Token already analyzed successfully, skipping');
    //   return;
    // }

    // Create a unique identifier for this analysis request
    const analysisId = Date.now();
    currentAnalysisRef.current = analysisId;

    setAnalyzingToken(true);
    setLastAnalyzedToken(mintAddress);
    
    try {
      console.log(`Analyzing token: ${mintAddress} (ID: ${analysisId})`);
      const response = await api.get(`/trading-bot/tokens/${mintAddress}/analyze`);
      
      // Check if this is still the current analysis request
      if (currentAnalysisRef.current !== analysisId) {
        console.log(`Analysis ${analysisId} cancelled - newer request in progress`);
        return; // Abandon this request as a newer one is in progress
      }
      
      console.log(`Analysis ${analysisId} completed successfully`, response.data);
      setTokenAnalysis(response.data);
      
      // Auto-adjust USDC setting based on analysis
      if (response.data) {
        const analysis = response.data;
        
        // If token only supports USDC and USDC is currently disabled, auto-enable it
        if (analysis.supportsUSDC && !analysis.supportsSOL && !parameters.use_usdc_trading) {
          console.log('Token only supports USDC, auto-enabling USDC trading');
          setParameters(prev => ({
            ...prev,
            use_usdc_trading: true
          }));
        }
        
        // If token only supports SOL and USDC is currently enabled, auto-disable it
        if (analysis.supportsSOL && !analysis.supportsUSDC && parameters.use_usdc_trading) {
          console.log('Token only supports SOL, auto-disabling USDC trading');
          setParameters(prev => ({
            ...prev,
            use_usdc_trading: false
          }));
        }
      }
      
    } catch (error) {
      // Check if this is still the current analysis request
      if (currentAnalysisRef.current !== analysisId) {
        console.log(`Analysis ${analysisId} error cancelled - newer request in progress`);
        return;
      }
      
      console.error(`Error analyzing token (ID: ${analysisId}):`, error);
      setTokenAnalysis({
        status: 'error',
        error: error.response?.data?.error || 'Failed to analyze token'
      });
    } finally {
      // Only update analyzing state if this is still the current request
      if (currentAnalysisRef.current === analysisId) {
        setAnalyzingToken(false);
      }
    }
  };

  const handleIntervalBlur = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Only apply validation when user finishes editing (on blur)
    if (name.includes('interval') && value !== '') {
      const numValue = parseInt(value);
      
      // Enforce minimum of 600 seconds (10 minutes)
      if (numValue < 600) {
        processedValue = 600;
      }
      
      // Enforce maximum of 86400 seconds (1 day)
      if (numValue > 86400) {
        processedValue = 86400;
      }
      
      setParameters(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  const handlePercentageChange = (e) => {
    const { name, value } = e.target;
    const decimalValue = value ? parseFloat(value) / 100 : 0;
    setParameters(prev => ({
      ...prev,
      [name]: decimalValue
    }));
    setError('');
  };

  const saveParameters = async () => {
    try {
      setLoading(true);
      setSaveStatus('');
      
      // Validate required fields
      if (!parameters.mint_address || parameters.mint_address.trim() === '') {
        setError('Token mint address is required.');
        setLoading(false);
        return;
      }
      
      // Validate interval ranges (convert to numbers and handle empty values)
      const whaleMin = parseInt(parameters.whale_interval_min) || 0;
      const whaleMax = parseInt(parameters.whale_interval_max) || 0;
      const retailMin = parseInt(parameters.retail_interval_min) || 0;
      const retailMax = parseInt(parameters.retail_interval_max) || 0;
      
      if (whaleMin < 600 || whaleMax > 86400 || whaleMin === 0 || whaleMax === 0) {
        setError('Whale intervals must be between 600 seconds (10 min) and 86400 seconds (1 day).');
        setLoading(false);
        return;
      }
      
      if (retailMin < 600 || retailMax > 86400 || retailMin === 0 || retailMax === 0) {
        setError('Retail intervals must be between 600 seconds (10 min) and 86400 seconds (1 day).');
        setLoading(false);
        return;
      }
      
      if (whaleMin > whaleMax) {
        setError('Whale interval minimum cannot be greater than maximum.');
        setLoading(false);
        return;
      }
      
      if (retailMin > retailMax) {
        setError('Retail interval minimum cannot be greater than maximum.');
        setLoading(false);
        return;
      }
      
      console.log('Saving parameters:', {
        hour_threshold: parameters.hour_threshold,
        day_threshold: parameters.day_threshold,
        volatility_multiplier: parameters.volatility_multiplier,
        use_advanced_settings: parameters.use_advanced_settings
      });
      
      await api.post('/trading-bot/trading-parameters', {
        ...parameters,
        groupId: selectedGroupId,
        status: 'active' // Auto-activate when saving
      });
      
      // Update local state
      setParameters(prev => ({ ...prev, status: 'active' }));
      setSaveStatus('Parameters saved and activated successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving parameters:', error);
      // Show the actual error message from the backend if available
      const errorMessage = error.response?.data?.error || 'Failed to save parameters. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleBot = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (botStatus === 'inactive') {
        // Validate parameters before starting
        if (!parameters.mint_address || parameters.mint_address.trim() === '') {
          setError('Token mint address is required.');
          setLoading(false);
          return;
        }
        
        // Check if parameters are saved
        if (parameters.status !== 'active') {
          setError('Please save your configuration before starting the bot.');
          setLoading(false);
          return;
        }
        
        // Start bot and update database configuration status
        setSaveStatus('Starting bot...');
        const response = await api.post('/trading-bot/bot/start', {
          groupId: selectedGroupId,
          mintAddress: parameters.mint_address || parameters.token_address,
          updateConfigStatus: true // Signal to update database config status
        });
        
        if (response.data.success) {
          setBotStatus('active');
          setActiveBotId(response.data.botId);
          setSaveStatus('Bot started successfully!');
          setTimeout(() => setSaveStatus(''), 3000);
          
          // Refresh parameters to get updated database status
          await fetchParameters();
        }
      } else {
        // Stop bot and update database configuration status
        setSaveStatus('Stopping bot...');
        const response = await api.post('/trading-bot/bot/stop', {
          botId: activeBotId,
          groupId: selectedGroupId,
          updateConfigStatus: true // Signal to update database config status to inactive
        });
        
        if (response.data.success) {
          setBotStatus('inactive');
          setActiveBotId(null);
          setSaveStatus('Bot stopped successfully.');
          setTimeout(() => setSaveStatus(''), 3000);
          
          // Refresh parameters to get updated database status
          await fetchParameters();
          
          // Temporarily disable status polling to prevent immediate restart detection
          setStopCooldown(true);
          setTimeout(() => {
            setStopCooldown(false);
            console.log('Stop cooldown ended, resuming status checks');
          }, 15000); // Increased to 15 seconds to ensure backend processes the stop
        }
      }
    } catch (error) {
      console.error('Error toggling bot:', error);
      const errorMsg = error.response?.data?.error || error.message;
      setError(`Failed to ${botStatus === 'inactive' ? 'start' : 'stop'} bot: ${errorMsg}`);
      
      // Refresh status on error to ensure UI reflects reality
      setTimeout(fetchBotStatus, 1000);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (botStatus) {
      case 'active':
        return <Activity className="w-4 h-4 text-green-500" />;
      case 'starting':
        return <Zap className="w-4 h-4 text-yellow-500 animate-pulse" />;
      case 'stopping':
        return <Square className="w-4 h-4 text-orange-500" />;
      default:
        return <Bot className="w-4 h-4 text-gray-400 dark:text-gray-500" />;
    }
  };

  const getStatusColor = () => {
    switch (botStatus) {
      case 'active':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 'starting':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      case 'stopping':
        return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700';
    }
  };

  if (!selectedGroupId) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6 transition-colors">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Trading Bot</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">Select a wallet group to configure trading</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg mb-6 overflow-hidden border border-gray-100 dark:border-gray-700 transition-colors">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0 transition-colors">
              <Bot className="w-5 h-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Trading Bot</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {groupName || 'Selected Group'} • Automated wash trading
              </p>
              {/* Parameters Status Indicator */}
              <div className="flex items-center mt-1">
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  parameters.status === 'active' && parameters.mint_address ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {parameters.status === 'active' && parameters.mint_address 
                    ? 'Parameters configured' 
                    : 'Parameters required'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            {/* Status Badge */}
            <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor()}`}>
              {getStatusIcon()}
              <span className="capitalize">{botStatus}</span>
            </div>
            
            {/* Bot Control Button */}
            <button
              onClick={toggleBot}
              disabled={loading || (botStatus === 'inactive' && (parameters.status !== 'active' || !parameters.mint_address))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md ${
                botStatus === 'active' 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
              }`}
              title={
                loading 
                  ? 'Processing request...' 
                  : botStatus === 'inactive' && parameters.status !== 'active'
                    ? 'Please save your configuration first'
                    : botStatus === 'inactive' && !parameters.mint_address
                      ? 'Token mint address is required'
                      : botStatus === 'active' 
                        ? 'Stop the trading bot'
                        : 'Start the trading bot'
              }
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{botStatus === 'active' ? 'Stopping...' : 'Starting...'}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  {botStatus === 'active' ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  <span>{botStatus === 'active' ? 'Stop Bot' : 'Start Bot'}</span>
                </div>
              )}
            </button>
            
            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-start space-x-2 transition-colors">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
        
        {/* Save Status */}
        {saveStatus && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg transition-colors">
            <p className="text-sm text-green-700 dark:text-green-300">{saveStatus}</p>
          </div>
        )}
      </div>

      {/* Expanded Configuration */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 transition-colors">
          {/* Save button at the top for visibility */}
          <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {parameters.status === 'active' ? '✓ Configuration is saved' : 'Configure your bot parameters below'}
              </p>
              <button
                onClick={saveParameters}
                disabled={loading || !parameters.mint_address}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Configuration</span>
              </button>
            </div>
          </div>
          
          <div className="p-4 sm:p-6 max-h-[400px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Token Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 space-y-3 transition-colors">
              <div className="flex items-center space-x-2 pb-2 border-b border-gray-100 dark:border-gray-600">
                <Target className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Token Settings</h3>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                  Token Mint Address *
                </label>
                <input
                  type="text"
                  name="mint_address"
                  value={parameters.mint_address || ''}
                  onChange={handleParameterChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                  placeholder="Enter token mint address"
                />
                
                {/* Token Analysis Status */}
                {analyzingToken && (
                  <div className="mt-2 flex items-center text-blue-600 dark:text-blue-400 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400 mr-2"></div>
                    Analyzing token...
                  </div>
                )}
                
                {tokenAnalysis && tokenAnalysis.status !== 'error' && (
                  <div className="mt-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-blue-900 dark:text-blue-100 text-sm">
                          Token Status: {tokenAnalysis.location}
                        </div>
                        <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Available trading options:
                        </div>
                        <div className="flex gap-2 mt-2">
                          {tokenAnalysis.supportsUSDC && (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded text-xs flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              USDC
                            </span>
                          )}
                          {tokenAnalysis.supportsSOL && (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded text-xs flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              SOL
                            </span>
                          )}
                          {!tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL && (
                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded text-xs">
                              No trading options available
                            </span>
                          )}
                        </div>
                        {tokenAnalysis.recommendations && tokenAnalysis.recommendations.length > 0 && (
                          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                            {tokenAnalysis.recommendations.map((rec, index) => (
                              <div key={index} className="flex items-start mt-1">
                                <span className="mr-1">•</span>
                                <span>{rec}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {tokenAnalysis && tokenAnalysis.status === 'error' && (
                  <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
                    <div className="flex items-center text-red-800 dark:text-red-300 text-sm">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {tokenAnalysis.error}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Price Min
                  </label>
                  <input
                    type="number"
                    name="price_range_min"
                    value={parameters.price_range_min}
                    onChange={handleParameterChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    step="0.000001"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Price Max
                  </label>
                  <input
                    type="number"
                    name="price_range_max"
                    value={parameters.price_range_max}
                    onChange={handleParameterChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    step="0.000001"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                  Whale Threshold ({parameters.use_usdc_trading ? 'USDC' : 'SOL'})
                </label>
                <input
                  type="number"
                  name={parameters.use_usdc_trading ? "whale_threshold_usdc" : "whale_threshold"}
                  value={parameters.use_usdc_trading ? 
                    (parameters.whale_threshold_usdc !== undefined ? parameters.whale_threshold_usdc : '') : 
                    parameters.whale_threshold}
                  onChange={handleParameterChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                  step={parameters.use_usdc_trading ? "10" : "0.1"}
                  placeholder={parameters.use_usdc_trading ? "100" : "5"}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {parameters.use_usdc_trading 
                    ? "Amount in USDC to consider a whale trade"
                    : "Amount in SOL to consider a whale trade"}
                </p>
              </div>

              {/* Smart USDC Trading Toggle */}
              <div className={`p-3 border rounded-lg transition-colors ${
                tokenAnalysis && tokenAnalysis.status !== 'error' ? 
                  (tokenAnalysis.supportsUSDC && tokenAnalysis.supportsSOL ? 
                    'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                    tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL ?
                    'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700' :
                    !tokenAnalysis.supportsUSDC && tokenAnalysis.supportsSOL ?
                    'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700' :
                    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                  ) :
                  'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Use USDC for Trading</span>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {tokenAnalysis && tokenAnalysis.status !== 'error' ? (
                          tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL ? (
                            <span className="text-amber-600 dark:text-amber-400 flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              This token only supports USDC trading
                            </span>
                          ) : !tokenAnalysis.supportsUSDC && tokenAnalysis.supportsSOL ? (
                            <span className="text-amber-600 dark:text-amber-400 flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              This token only supports SOL trading
                            </span>
                          ) : tokenAnalysis.supportsUSDC && tokenAnalysis.supportsSOL ? (
                            <span className="text-green-600 dark:text-green-400">Both USDC and SOL trading available</span>
                          ) : (
                            <span className="text-red-600 dark:text-red-400">No trading options available</span>
                          )
                        ) : (
                          'Trade with USDC instead of SOL'
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleParameterChange({
                      target: { name: 'use_usdc_trading', value: !parameters.use_usdc_trading }
                    })}
                    disabled={tokenAnalysis && tokenAnalysis.status !== 'error' && (
                      !tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL
                    )}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                      tokenAnalysis && tokenAnalysis.status !== 'error' && !tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL
                        ? 'opacity-50 cursor-not-allowed bg-gray-300 dark:bg-gray-500' :
                        parameters.use_usdc_trading ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-200 transition-transform ${
                        parameters.use_usdc_trading ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Contextual status messages */}
                {tokenAnalysis && tokenAnalysis.status !== 'error' && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    {tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL && parameters.use_usdc_trading && (
                      <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-md p-2">
                        <div className="flex items-center text-green-800 dark:text-green-300 text-xs">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            USDC trading automatically enabled for this pre-bonding curve token.
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!tokenAnalysis.supportsUSDC && tokenAnalysis.supportsSOL && !parameters.use_usdc_trading && (
                      <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-md p-2">
                        <div className="flex items-center text-green-800 dark:text-green-300 text-xs">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            SOL trading automatically configured for this token.
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {tokenAnalysis.supportsUSDC && tokenAnalysis.supportsSOL && (
                      <div className="bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md p-2">
                        <div className="flex items-center text-blue-800 dark:text-blue-300 text-xs">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM9 16a3 3 0 11-6 0 3 3 0 016 0zM17 16a3 3 0 11-6 0 3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>Flexible:</strong> This token supports both USDC and SOL trading. Choose your preferred option above.
                          </span>
                        </div>
                      </div>
                    )}

                    {!tokenAnalysis.supportsUSDC && !tokenAnalysis.supportsSOL && (
                      <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md p-2">
                        <div className="flex items-center text-red-800 dark:text-red-300 text-xs">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span>
                            <strong>Issue:</strong> This token has no available trading options. Please verify the token address.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* Whale Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 space-y-3 transition-colors">
              <div className="flex items-center space-x-2 pb-2 border-b border-gray-100 dark:border-gray-600">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Whale Trading</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Trade % Min
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="whale_trade_percent_min"
                      value={(parameters.whale_trade_percent_min * 100).toFixed(1)}
                      onChange={handlePercentageChange}
                      className="w-full px-2 py-1.5 pr-6 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-xs transition-colors">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Trade % Max
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="whale_trade_percent_max"
                      value={(parameters.whale_trade_percent_max * 100).toFixed(1)}
                      onChange={handlePercentageChange}
                      className="w-full px-2 py-1.5 pr-6 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-xs transition-colors">%</span>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                  Buy Probability
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="whale_buy_probability"
                    value={(parameters.whale_buy_probability * 100).toFixed(0)}
                    onChange={handlePercentageChange}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    min="0"
                    max="100"
                  />
                  <span className="absolute right-3 top-2 text-gray-500 dark:text-gray-400 text-sm transition-colors">%</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Interval Min (s)
                    <span className="text-gray-500 dark:text-gray-400 font-normal ml-1 transition-colors">(600-86400)</span>
                  </label>
                  <input
                    type="number"
                    name="whale_interval_min"
                    value={parameters.whale_interval_min}
                    onChange={handleParameterChange}
                    onBlur={handleIntervalBlur}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    title="Min: 600 seconds (10 min), Max: 86400 seconds (1 day)"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Interval Max (s)
                    <span className="text-gray-500 dark:text-gray-400 font-normal ml-1 transition-colors">(600-86400)</span>
                  </label>
                  <input
                    type="number"
                    name="whale_interval_max"
                    value={parameters.whale_interval_max}
                    onChange={handleParameterChange}
                    onBlur={handleIntervalBlur}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    title="Min: 600 seconds (10 min), Max: 86400 seconds (1 day)"
                  />
                </div>
              </div>
            </div>

            {/* Retail Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 space-y-3 transition-colors">
              <div className="flex items-center space-x-2 pb-2 border-b border-gray-100 dark:border-gray-600">
                <BarChart3 className="w-4 h-4 text-green-600" />
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Retail Trading</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Trade % Min
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="retail_trade_percent_min"
                      value={(parameters.retail_trade_percent_min * 100).toFixed(1)}
                      onChange={handlePercentageChange}
                      className="w-full px-2 py-1.5 pr-6 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-xs transition-colors">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Trade % Max
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="retail_trade_percent_max"
                      value={(parameters.retail_trade_percent_max * 100).toFixed(1)}
                      onChange={handlePercentageChange}
                      className="w-full px-2 py-1.5 pr-6 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span className="absolute right-2 top-1.5 text-gray-500 dark:text-gray-400 text-xs transition-colors">%</span>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                  Buy Probability
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="retail_buy_probability"
                    value={(parameters.retail_buy_probability * 100).toFixed(0)}
                    onChange={handlePercentageChange}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    min="0"
                    max="100"
                  />
                  <span className="absolute right-3 top-2 text-gray-500 dark:text-gray-400 text-sm transition-colors">%</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Interval Min (s)
                    <span className="text-gray-500 dark:text-gray-400 font-normal ml-1 transition-colors">(600-86400)</span>
                  </label>
                  <input
                    type="number"
                    name="retail_interval_min"
                    value={parameters.retail_interval_min}
                    onChange={handleParameterChange}
                    onBlur={handleIntervalBlur}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    title="Min: 600 seconds (10 min), Max: 86400 seconds (1 day)"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Interval Max (s)
                    <span className="text-gray-500 dark:text-gray-400 font-normal ml-1 transition-colors">(600-86400)</span>
                  </label>
                  <input
                    type="number"
                    name="retail_interval_max"
                    value={parameters.retail_interval_max}
                    onChange={handleParameterChange}
                    onBlur={handleIntervalBlur}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    title="Min: 300 seconds (5 min), Max: 86400 seconds (1 day)"
                  />
                </div>
              </div>

            </div>
            
            {/* Advanced Settings in its own card */}
            <div className="col-span-full bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center space-x-2">
                  <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400 transition-colors" />
                  <span>Advanced Settings</span>
                </h3>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400 transition-colors">Enable</span>
                  <button
                    type="button"
                    onClick={() => handleParameterChange({
                      target: { name: 'use_advanced_settings', value: !parameters.use_advanced_settings }
                    })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                      parameters.use_advanced_settings ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white dark:bg-gray-200 transition-transform ${
                        parameters.use_advanced_settings ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              {parameters.use_advanced_settings && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    1h Threshold (%)
                  </label>
                  <input
                    type="number"
                    name="hour_threshold"
                    value={parameters.hour_threshold !== undefined && parameters.hour_threshold !== null 
                      ? (parameters.hour_threshold * 100).toString()
                      : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleParameterChange({
                        target: { 
                          name: 'hour_threshold', 
                          value: value === '' ? '' : parseFloat(value) / 100
                        }
                      });
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parameters.hour_threshold === '') {
                        handleParameterChange({
                          target: { name: 'hour_threshold', value: 0.01 }
                        });
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    placeholder="1.00"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    24h Threshold (%)
                  </label>
                  <input
                    type="number"
                    name="day_threshold"
                    value={parameters.day_threshold !== undefined && parameters.day_threshold !== null 
                      ? (parameters.day_threshold * 100).toString()
                      : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleParameterChange({
                        target: { 
                          name: 'day_threshold', 
                          value: value === '' ? '' : parseFloat(value) / 100
                        }
                      });
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parameters.day_threshold === '') {
                        handleParameterChange({
                          target: { name: 'day_threshold', value: 0.05 }
                        });
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    placeholder="5.00"
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Volatility Multiplier (vs BTC)
                  </label>
                  <input
                    type="number"
                    name="volatility_multiplier"
                    value={parameters.volatility_multiplier !== undefined && parameters.volatility_multiplier !== null 
                      ? parameters.volatility_multiplier.toString()
                      : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleParameterChange({
                        target: { 
                          name: 'volatility_multiplier', 
                          value: value === '' ? '' : parseFloat(value)
                        }
                      });
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parameters.volatility_multiplier === '') {
                        handleParameterChange({
                          target: { name: 'volatility_multiplier', value: 5 }
                        });
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    placeholder="5.0"
                    step="0.1"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Aggressiveness (0-1)
                  </label>
                  <input
                    type="number"
                    name="aggressiveness"
                    value={parameters.aggressiveness || 0.5}
                    onChange={handleParameterChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                    step="0.05"
                    min="0"
                    max="1"
                  />
                </div>
                </div>
              )}
              {!parameters.use_advanced_settings && (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400 transition-colors">
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">Advanced settings are disabled. Enable the toggle above to configure price following parameters.</p>
                </div>
              )}
            </div>
            
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingBot;